import { useEffect, useRef, useState } from 'react';
import type { SharePermission } from '../api';
import { useShares, useUnshare } from '../library/teamQueries';
import {
  useResendSnippetInvitation,
  useRevokeSnippetInvitation,
  useShareSnippetByInvitation,
  useSnippetInvitations,
} from '../library/invitationQueries';
import { Banner, Button, Field, Spinner, TextInput } from './ui';
import { errorMessage } from '../api/problems';

interface ShareDialogProps {
  snippetId: string;
  title: string;
  onClose: () => void;
}

export function ShareDialog({ snippetId, title, onClose }: ShareDialogProps) {
  const shares = useShares(snippetId, true);
  const invitations = useSnippetInvitations(snippetId, true);
  const share = useShareSnippetByInvitation(snippetId);
  const unshare = useUnshare(snippetId);
  const revoke = useRevokeSnippetInvitation(snippetId);
  const resend = useResendSnippetInvitation(snippetId);

  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<SharePermission>('Read');
  const [invited, setInvited] = useState<string | null>(null);

  const waiting = (invitations.data ?? []).filter(
    (invitation) => invitation.status === 'Pending',
  );
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus moves into the dialog on open, and Escape closes it — the two things a keyboard user
  // needs from anything modal.
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onPointerDown={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${title}`}
        onPointerDown={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-border-subtle bg-surface p-5 shadow-2xl"
      >
        <h2 className="text-base font-semibold text-text">Share this snippet</h2>
        <p className="mt-1 text-sm text-text-muted">
          They get an email and choose whether to accept. Read access lets them read it; write
          access lets them edit it too — but not delete it or pass it on.
        </p>

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            share.mutate(
              { email, permission },
              {
                onSuccess: () => {
                  setInvited(email);
                  setEmail('');
                },
              },
            );
          }}
        >
          {share.isError && <Banner tone="error">{errorMessage(share.error)}</Banner>}

          {/*
            Says nothing about whether that address has an account here — the endpoint answers the
            same either way, and a message that gave it away would put the leak back through the UI.
          */}
          {invited && !share.isError && (
            <Banner tone="success">Invitation sent to {invited}.</Banner>
          )}

          <Field label="Email address">
            {(id, describedBy) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="email"
                required
                placeholder="colleague@example.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setInvited(null);
                }}
              />
            )}
          </Field>

          <fieldset className="flex items-center gap-4">
            <legend className="sr-only">Permission</legend>

            {(['Read', 'Write'] as const).map((option) => (
              <label key={option} className="flex items-center gap-1.5 text-sm text-text">
                <input
                  type="radio"
                  name="permission"
                  value={option}
                  checked={permission === option}
                  onChange={() => setPermission(option)}
                  className="accent-[var(--accent)]"
                />
                {option === 'Read' ? 'Can read' : 'Can edit'}
              </label>
            ))}
          </fieldset>

          <Button type="submit" variant="primary" disabled={share.isPending}>
            {share.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </form>

        {waiting.length > 0 && (
          <div className="mt-5">
            <h3 className="text-xs font-medium tracking-wide text-text-faint uppercase">
              Waiting to be accepted
            </h3>

            {revoke.isError && (
              <div className="mt-2">
                <Banner tone="error">{errorMessage(revoke.error)}</Banner>
              </div>
            )}

            <ul className="mt-2 flex flex-col gap-1">
              {waiting.map((invitation) => (
                <li
                  key={invitation.id}
                  className="flex items-center justify-between gap-2 rounded-md
                    bg-surface-raised px-2 py-1.5 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-text">{invitation.email}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {invitation.permission === 'Write' ? 'can edit' : 'can read'} · not accepted yet
                    </span>
                  </span>

                  <span className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(invitation.id)}
                    >
                      Resend
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={`Withdraw the invitation to ${invitation.email}`}
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(invitation.id)}
                    >
                      Withdraw
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5">
          <h3 className="text-xs font-medium tracking-wide text-text-faint uppercase">
            Shared with
          </h3>

          {shares.isPending ? (
            <div className="mt-2">
              <Spinner label="Loading" />
            </div>
          ) : shares.data?.length === 0 ? (
            <p className="mt-2 text-sm text-text-muted">Nobody yet.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-1">
              {shares.data?.map((recipient) => (
                <li
                  key={recipient.userId}
                  className="flex items-center justify-between gap-2 rounded-md
                    bg-surface-raised px-2 py-1.5 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-text">{recipient.displayName}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {recipient.email} · {recipient.permission === 'Write' ? 'can edit' : 'can read'}
                    </span>
                  </span>

                  <Button
                    variant="ghost"
                    aria-label={`Stop sharing with ${recipient.displayName}`}
                    onClick={() => unshare.mutate(recipient.userId)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
